/**
 * Flow Exporter for Flow2
 *
 * Wraps compiled flows in the flowboard document structure and exports
 * them via the /flow/new/importJson endpoint.
 *
 * @packageDocumentation
 * @module ai/compiler
 */

import Flow from '../../model/Flow';
import { guidGenerator, getCurrentDate } from '../../features/utils/commonFunctions';
import { StyleTheme } from '../schemas/StyleTheme';
import { FLOWVELLA_THEME_MAP } from '../styles/themeLibrary';
import { PresentationContent } from '../schemas/ContentBlock';
import { ContentMapping } from './FlowCompiler';

/**
 * Content layer stored with the flow
 */
export interface FlowContent {
  presentation: PresentationContent;
  themeId: string;
  layoutIds: string[];
  lastCompiled: string;
  sessionId?: string;
}

/**
 * Flowboard document structure (wraps the Flow)
 */
export interface FlowboardDocument {
  flowboard: {
    flows: Flow[];
    title: string;
    created: string;
    updated: string;
    id: string;
    docVersion: string;
    themeId?: string;
    themeStringId?: string;
    sideMenuOpen?: string;
    canRemix?: string;
    isThemeFlowboard?: string;
    // NEW: Content layer for AI-assisted editing
    content?: FlowContent;
    contentMapping?: ContentMapping;
  };
}

/**
 * Export result from the API
 */
export interface FlowExportResult {
  success: boolean;
  stringId?: string;
  editUrl?: string;
  viewUrl?: string;
  error?: string;
}

/**
 * Options for flow export
 */
export interface FlowExportOptions {
  title?: string;
  theme?: StyleTheme;
  autoRedirect?: boolean;
  // Content layer options
  presentation?: PresentationContent;
  layoutIds?: string[];
  contentMapping?: ContentMapping;
  sessionId?: string;
}

/**
 * Wrap a compiled Flow in the flowboard document structure
 */
export function wrapFlowInDocument(flow: Flow, options: FlowExportOptions = {}): FlowboardDocument {
  const title = options.title || flow.title || 'AI Generated Presentation';
  const now = getCurrentDate();

  // Get theme IDs
  let themeId = flow.themeId || '10';
  let themeStringId = FLOWVELLA_THEME_MAP[themeId] || 'dyl';

  if (options.theme) {
    themeId = options.theme.flowvellaThemeId || themeId;
    themeStringId = options.theme.flowvellaThemeStringId || themeStringId;
  }

  const document: FlowboardDocument = {
    flowboard: {
      flows: [flow],
      title,
      created: now,
      updated: now,
      id: guidGenerator(),
      docVersion: '1.3',
      themeId,
      themeStringId,
      sideMenuOpen: 'NO',
      canRemix: 'YES',
      isThemeFlowboard: 'NO',
    },
  };

  // Include content layer if provided
  if (options.presentation) {
    document.flowboard.content = {
      presentation: options.presentation,
      themeId: options.theme?.id || 'default',
      layoutIds: options.layoutIds || [],
      lastCompiled: now,
      ...(options.sessionId ? { sessionId: options.sessionId } : {}),
    };
  }

  // Include content mapping if provided
  if (options.contentMapping) {
    document.flowboard.contentMapping = options.contentMapping;
  }

  return document;
}

/**
 * Serialize a Flow to JSON suitable for export
 */
export function serializeFlow(flow: Flow): object {
  // Deep clone and clean up internal properties
  const serialized = JSON.parse(JSON.stringify(flow, (key, value) => {
    // Remove internal properties that start with __ (but preserve __tag which is a data-model field)
    if (key.startsWith('__') && key !== '__tag') {
      return undefined;
    }
    return value;
  }));

  return serialized;
}

/**
 * Serialize the entire flowboard document
 */
export function serializeDocument(document: FlowboardDocument): string {
  return JSON.stringify(document, (key, value) => {
    // Remove internal properties (but preserve __tag which is a data-model field for AssetURL)
    if (key.startsWith('__') && key !== '__tag') {
      return undefined;
    }
    // Handle Color objects that need special serialization
    if (value && typeof value === 'object' && 'r' in value && 'g' in value && 'b' in value) {
      // This is a Color object - use iOS format
      const r = Math.floor(value.r).toString(16).padStart(2, '0');
      const g = Math.floor(value.g).toString(16).padStart(2, '0');
      const b = Math.floor(value.b).toString(16).padStart(2, '0');
      const a = Math.floor((value.a <= 1 ? value.a * 255 : value.a)).toString(16).padStart(2, '0');
      return `${r}${g}${b}${a}`;
    }
    return value;
  }, 2);
}

/**
 * Export a compiled flow to the FlowVella system
 */
export async function exportFlow(
  flow: Flow,
  options: FlowExportOptions = {}
): Promise<FlowExportResult> {
  try {
    // Wrap in document structure
    const document = wrapFlowInDocument(flow, options);

    // Serialize to JSON
    const jsonString = serializeDocument(document);

    // Send to import endpoint
    const response = await fetch('/flow/new/importJson', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        flow_json: JSON.parse(jsonString),
        title: options.title || flow.title,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      return {
        success: false,
        error: `Failed to import flow: ${response.status} ${errorText}`,
      };
    }

    const result = await response.json();

    // Generate URLs
    const stringId = result.string_id || result.stringId;
    const baseUrl = window.location.origin;

    const exportResult: FlowExportResult = {
      success: true,
      stringId,
      editUrl: stringId ? `${baseUrl}/flow/${stringId}/edit` : undefined,
      viewUrl: stringId ? `${baseUrl}/p/${stringId}` : undefined,
    };

    // Auto-redirect if requested
    if (options.autoRedirect && exportResult.editUrl) {
      window.location.href = exportResult.editUrl;
    }

    return exportResult;
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error during export',
    };
  }
}

/**
 * Download a flow as a JSON file
 */
export function downloadFlowAsJson(flow: Flow, filename?: string): void {
  const document = wrapFlowInDocument(flow);
  const jsonString = serializeDocument(document);

  const blob = new Blob([jsonString], { type: 'application/json' });
  const url = URL.createObjectURL(blob);

  const link = window.document.createElement('a');
  link.href = url;
  link.download = filename || `${flow.title || 'presentation'}.json`;
  window.document.body.appendChild(link);
  link.click();
  window.document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Preview URL generator for a screen
 */
export function getScreenPreviewUrl(stringId: string, screenIndex: number): string {
  return `${window.location.origin}/p/${stringId}#/screen/${screenIndex + 1}`;
}

/**
 * Validate a flow before export
 */
export function validateFlow(flow: Flow): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  // Check basic structure
  if (!flow.screens || flow.screens.length === 0) {
    errors.push('Flow must have at least one screen');
  }

  // Check each screen
  flow.screens.forEach((screen, index) => {
    if (!screen.id) {
      errors.push(`Screen ${index + 1} is missing an ID`);
    }

    // Check for valid background color
    if (!screen.FlowScreenModelBackgroundColorKey) {
      errors.push(`Screen ${index + 1} is missing a background color`);
    }
  });

  // Check view size
  if (!flow.viewSize || !flow.viewSize.width || !flow.viewSize.height) {
    errors.push('Flow must have valid viewSize dimensions');
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * Fix common issues in a flow before export
 */
export function sanitizeFlow(flow: Flow): Flow {
  // Ensure all required fields are present
  if (!flow.id) {
    flow.id = guidGenerator();
  }

  if (!flow.created) {
    flow.created = getCurrentDate();
  }

  if (!flow.updated) {
    flow.updated = getCurrentDate();
  }

  if (!flow.themeId) {
    flow.themeId = '10';
  }

  if (!flow.autoAdvanceLoops) {
    flow.autoAdvanceLoops = 'NO';
  }

  // Sanitize each screen
  flow.screens = flow.screens.map((screen) => sanitizeScreen(screen));

  return flow;
}

/**
 * Sanitize a single screen
 */
function sanitizeScreen(screen: any): any {
  if (!screen.id) {
    screen.id = guidGenerator();
  }

  if (!screen.created) {
    screen.created = getCurrentDate();
  }

  if (!screen.updated) {
    screen.updated = getCurrentDate();
  }

  if (!screen.previewScreenShotInvalidated) {
    screen.previewScreenShotInvalidated = 'yes';
  }

  // Ensure previewScreenShot exists (required for screen preview capture/upload)
  if (!screen.previewScreenShot) {
    screen.previewScreenShot = {
      created: getCurrentDate(),
      externalURL: { __tag: 'string', value: '' },
      id: guidGenerator(),
      isProcessed: 'no',
      isUploaded: 'no',
      treemoContentID: '',
      updated: getCurrentDate(),
    };
  }
  if (!screen.previewScreenShot_textless) {
    screen.previewScreenShot_textless = {
      created: getCurrentDate(),
      externalURL: { __tag: 'string', value: '' },
      id: guidGenerator(),
      isProcessed: 'no',
      isUploaded: 'no',
      treemoContentID: '',
      updated: getCurrentDate(),
    };
  }

  // Ensure mixedMediaObjects is an array
  if (!Array.isArray(screen.mixedMediaObjects)) {
    screen.mixedMediaObjects = [];
  }

  return screen;
}
